import React, { useRef, useState } from 'react'
import { useHistory } from 'react-router'
import { useDispatch } from 'react-redux'
import { ArrowLeft, FilePlus2, FolderOpen, Loader2 } from 'lucide-react'
import {
    clearAiProject,
    openAiProjectFile,
    AI_PROJECT_EXT,
} from './aiProjectStore'
import { clearCurrentProjectId } from './aiProjectDb'
import styles from './AiProject.module.css'
import MainHeader from '../ReusableComponents/Header/MainHeader'
import {

    getConnectedDeviceNameWithVersion
} from '../../hooks/useBrandConfig'
import PrgmSelection from '../ReusableComponents/PrgmSlider/PrgmSelection/PrgmSelection'

const ProjectCard = ({ icon: Icon, title, subtitle, onClick, disabled, spinning, size }) => (
    <button
        type="button"
        className={styles.card}
        onClick={onClick}
        disabled={disabled}
    >
        <span
            className={`${styles.cardIcon} ${spinning ? styles.spin : ''}`}
        >
            <Icon width={size} height={size} />
        </span>
        <span className={styles.cardTitle}>{title}</span>
        {/* <span className={styles.cardSubtitle}>{subtitle}</span> */}
    </button>
)

const AiProject = () => {
    const history = useHistory()
    const dispatch = useDispatch()
    const fileRef = useRef(null)
    const [busy, setBusy] = useState('')
    const [error, setError] = useState('')

    const goBack = () => {
        sessionStorage.setItem('slideDirection', true)
        history.push('/Selection')
    }

    const onNewProject = async () => {
        if (busy) return
        setError('')
        setBusy('new')
        try {
            await clearAiProject()
            // A fresh project isn't tied to a saved one yet, so Save creates a
            // new library entry rather than overwriting the last-opened project.
            clearCurrentProjectId()
            // Flag AI-Studio context up-front so the header Save produces an AI
            // project file even before the user reaches the Code step.
            sessionStorage.setItem('webAiMode', 'true')
            // Reset the Redux trainMachine slice to defaults (storage was just
            // cleared) so a stale model from a previous project doesn't linger.
            dispatch({ type: 'HYDRATE_APP_MODE_STORAGE' })
            history.push('/ai-code')
        } catch (e) {
            setError('Could not start a new project. Please try again.')
            setBusy('')
        }
    }

    const onOpenProject = () => {
        if (busy) return
        setError('')
        fileRef.current && fileRef.current.click()
    }

    const onYourProjects = () => {
        if (busy) return
        setError('')
        sessionStorage.setItem('slideDirection', true)
        history.push('/ai-showcase')
    }

    const onFilePicked = async (e) => {
        const file = e.target.files && e.target.files[0]
        e.target.value = ''
        if (!file) return
        setError('')
        setBusy('open')
        try {
            await openAiProjectFile(file)
            // Imported from a file, not the library — Save should make a new
            // library entry rather than overwrite an unrelated saved project.
            clearCurrentProjectId()
            // Refresh the Redux trainMachine slice from the just-restored storage
            // so the imported models/classes/images appear WITHOUT a full page
            // reload (keeps navigation smooth, no flash/lag).
            dispatch({ type: 'HYDRATE_APP_MODE_STORAGE' })
            history.push('/ai-code')
        } catch (err) {
            setError(
                err && err.message
                    ? err.message
                    : 'Could not open this project file.'
            )
            setBusy('')
        }
    }

    const NewProject = (props) => (<svg width="60" height="74" viewBox="0 0 60 74" fill="none" xmlns="http://www.w3.org/2000/svg"  {...props}>
        <path d="M58.6051 22.309L36.932 0.635986C36.7308 0.434399 36.4919 0.274477 36.2289 0.165344C35.9658 0.0562114 35.6838 2.43148e-05 35.399 0H2.16699C1.59235 0.000264996 1.04134 0.228645 0.63501 0.634979C0.228676 1.04131 0.000264996 1.59235 0 2.16699V71.81C0 72.3847 0.228374 72.9359 0.634766 73.3423C1.04116 73.7487 1.59227 73.977 2.16699 73.977H57.067C57.6417 73.977 58.193 73.7487 58.5994 73.3423C59.0058 72.9359 59.234 72.3847 59.234 71.81V23.84C59.2354 23.2665 59.0093 22.7159 58.6051 22.309ZM40.1431 39.134H31.7661V47.513C31.7653 48.0817 31.5388 48.6269 31.1365 49.0289C30.7341 49.4308 30.1888 49.6567 29.6201 49.657C29.0514 49.6567 28.506 49.4308 28.1036 49.0289C27.7013 48.6269 27.4748 48.0817 27.474 47.513V39.134H19.097C18.5281 39.1335 17.9825 38.9072 17.5802 38.5049C17.1779 38.1025 16.9516 37.557 16.951 36.988C16.9516 36.419 17.1779 35.8735 17.5802 35.4711C17.9825 35.0688 18.5281 34.8425 19.097 34.842H27.474V26.465C27.4745 25.896 27.7008 25.3505 28.1031 24.9481C28.5055 24.5458 29.0511 24.3195 29.6201 24.319C30.1891 24.3195 30.7346 24.5458 31.137 24.9481C31.5393 25.3505 31.7656 25.896 31.7661 26.465V34.842H40.1431C40.7121 34.8425 41.2576 35.0688 41.6599 35.4711C42.0623 35.8735 42.2885 36.419 42.2891 36.988C42.2885 37.557 42.0623 38.1025 41.6599 38.5049C41.2576 38.9072 40.7121 39.1335 40.1431 39.134Z" fill="url(#paint0_linear_2_10443)" />
        <defs>
            <linearGradient id="paint0_linear_2_10443" x1="7.88621e-07" y1="2.07136" x2="71.7413" y2="57.9068" gradientUnits="userSpaceOnUse">
                <stop stop-color="#FFBC45" />
                <stop offset="0.827" stop-color="#F87B62" />
                <stop offset="1" stop-color="#F16178" />
            </linearGradient>
        </defs>
    </svg>)

    const OpenProject = (props) => (<svg width="87" height="72" viewBox="0 0 87 72" fill="none" xmlns="http://www.w3.org/2000/svg"  {...props}>
        <path d="M85.7051 17.7289C85.4677 17.4638 85.1769 17.2518 84.8519 17.1068C84.5269 16.9617 84.1751 16.8868 83.8192 16.887H73.95V8.07794C73.9503 7.74571 73.8851 7.41669 73.7581 7.10968C73.6311 6.80267 73.4449 6.52373 73.21 6.28876C72.9752 6.05379 72.6962 5.86741 72.3892 5.7403C72.0823 5.61319 71.7534 5.54784 71.4212 5.54797H28.003V2.52997C28.0033 2.19765 27.9382 1.86854 27.8111 1.56146C27.6841 1.25439 27.4977 0.975403 27.2628 0.740418C27.0278 0.505434 26.7488 0.319069 26.4417 0.192017C26.1346 0.0649662 25.8055 -0.000262222 25.4732 7.92231e-07H2.5301C1.8591 7.92231e-07 1.2155 0.266501 0.741034 0.740968C0.266567 1.21543 6.68046e-05 1.85897 6.68046e-05 2.52997V68.6299C-0.00420369 69.2181 0.19639 69.7894 0.567205 70.246C0.804448 70.5368 1.10322 70.7712 1.44221 70.9322C1.78119 71.0932 2.15188 71.1768 2.52717 71.1769H78.3712C78.9938 71.1766 79.5944 70.9467 80.0582 70.5312C80.522 70.1157 80.8164 69.5439 80.8851 68.925L86.3311 19.6899C86.37 19.3375 86.3342 18.9808 86.2264 18.6429C86.1185 18.3051 85.941 17.9937 85.7051 17.7289ZM68.8902 16.887H12.6502C12.0675 16.8866 11.5026 17.0877 11.0511 17.4559C10.5995 17.8242 10.289 18.3371 10.1722 18.908L5.05915 43.777V5.06H22.9432V8.07898C22.9434 8.7499 23.21 9.39327 23.6844 9.86768C24.1588 10.3421 24.8023 10.6087 25.4732 10.6089H68.8902V16.887Z" fill="url(#paint0_linear_2_10437)" />
        <defs>
            <linearGradient id="paint0_linear_2_10437" x1="1.14959e-06" y1="1.99295" x2="70.2496" y2="84.8281" gradientUnits="userSpaceOnUse">
                <stop stop-color="#FFBC45" />
                <stop offset="0.827" stop-color="#F87B62" />
                <stop offset="1" stop-color="#F16178" />
            </linearGradient>
        </defs>
    </svg>)


    const YourProjects = (props) => (
        <svg width="100" height="100" viewBox="0 0 74 74" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
                <linearGradient id="yp_grad" x1="0" y1="0" x2="74" y2="74" gradientUnits="userSpaceOnUse">
                    <stop stop-color="#FFBC45"></stop>
                    <stop offset="0.827" stop-color="#F87B62"></stop>
                    <stop offset="1" stop-color="#F16178"></stop>
                </linearGradient>

                <mask id="yp_mask">
                    <rect width="74" height="74" fill="black"></rect>

                    <rect x="26" y="0" width="48" height="58" rx="6" fill="white"></rect>

                    <rect x="9" y="4" width="56" height="66" rx="10" fill="black"></rect>
                    <rect x="13" y="8" width="48" height="58" rx="6" fill="white"></rect>

                    =      <rect x="-4" y="12" width="56" height="66" rx="10" fill="black"></rect>
                    =      <rect x="0" y="16" width="48" height="58" rx="6" fill="white"></rect>
                </mask>
            </defs>

            <rect x="0" y="0" width="74" height="74" fill="url(#yp_grad)" mask="url(#yp_mask)"></rect>
        </svg>
    )

    return (
        <div className={styles.page}>
            {/* <button
                type="button"
                className={styles.back}
                onClick={goBack}
                aria-label="Back"
            >
                <ArrowLeft size={22} strokeWidth={2.2} />
            </button> */}
            <MainHeader
                title={getConnectedDeviceNameWithVersion()}
                titlePosition="left"
                isGoBack={true}
                goBack={goBack}
                showAccountSetting={false}
                helper={
                    <div
                        className="PS-S_slide"
                        style={{
                            left: '70%',
                            transform: 'translate(-50%)',
                        }}
                    >
                        <PrgmSelection />
                    </div>
                }
            // handleSleepClick={handleSleepClick}
            />
            <div className={styles.inner}>
                {/* <h1 className={styles.heading}>A.I. Studio</h1>
                <p className={styles.sub}>
                    Start a fresh project or open a saved one.
                </p> */}

                <div className={styles.cards}>
                    <ProjectCard
                        icon={busy === 'new' ? Loader2 : NewProject}
                        spinning={busy === 'new'}
                        title="New Project"
                        subtitle="Begin a blank A.I. Studio project"
                        onClick={onNewProject}
                        disabled={!!busy}
                        size={100}
                    />
                    <ProjectCard
                        icon={busy === 'open' ? Loader2 : OpenProject}
                        spinning={busy === 'open'}
                        title="Open Project"
                        subtitle={`Load a saved .${AI_PROJECT_EXT} project file`}
                        onClick={onOpenProject}
                        disabled={!!busy}
                        size={100}
                    />
                    <ProjectCard
                        icon={YourProjects}
                        title="Your Projects"
                        subtitle="Open a project saved on this device"
                        onClick={onYourProjects}
                        disabled={!!busy}
                        size={100}
                    />
                </div>

                {busy === 'open' && (
                    <p className={styles.busy}>Restoring project…</p>
                )}
                {error && <p className={styles.error}>{error}</p>}
            </div>

            <input
                ref={fileRef}
                type="file"
                accept={`.${AI_PROJECT_EXT},application/json`}
                style={{ display: 'none' }}
                onChange={onFilePicked}
            />
        </div>
    )
}

export default AiProject
